import {
  STANDARD_TARIFF_BASE_CODES,
  STANDARD_TARIFF_PERCENTAGE_CODES,
  getDefaultPercentageDependencies,
  getChargeDefinition,
  normalizePercentageAppliesTo,
} from './chargeMaster';

function calculateBillingDays(billingPeriod = {}) {
  if (!billingPeriod.startDate || !billingPeriod.endDate) {
    return 30;
  }

  const startDate = new Date(billingPeriod.startDate);
  const endDate = new Date(billingPeriod.endDate);
  const days = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  return Number.isFinite(days) && days > 0 ? days : 30;
}

function getRateValue(rate = {}) {
  return parseFloat(rate.rate) || 0;
}

function getCategoryCode(category = {}) {
  return category.category_code || category.categoryCode || category.code || '';
}

function getCategoryChargeType(category = {}) {
  const categoryCode = getCategoryCode(category);
  const chargeType = category.charge_type
    || category.chargeType
    || getChargeDefinition(categoryCode)?.chargeType
    || '';

  if (chargeType) {
    return String(chargeType).toLowerCase();
  }

  if (STANDARD_TARIFF_PERCENTAGE_CODES.includes(categoryCode)) {
    return 'percentage';
  }

  if (categoryCode === 'KWH_FIXED_CHARGE') {
    return 'unit';
  }

  if (categoryCode === 'KVA_PER_UNIT_CHARGE' || categoryCode === 'KVA_DEMAND_CHARGE') {
    return 'demand';
  }

  if (categoryCode === 'SERVICE_CHARGE') {
    return 'fixed';
  }

  return '';
}

function isBaseChargeCategory(category = {}) {
  const value = category.is_base_charge ?? category.isBaseCharge;
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return value === true;
}

function getPercentageAppliesTo(category = {}) {
  const appliesTo = category.percentage_applies_to || category.percentageAppliesTo || [];
  return normalizePercentageAppliesTo(appliesTo);
}

function isDailyRate(rate = {}) {
  return rate.rate_type === 'DAILY' || rate.rateType === 'DAILY';
}

function isStandardTariffBaseCharge(categoryCode = '') {
  return STANDARD_TARIFF_BASE_CODES.includes(categoryCode);
}

function isStandardTariffPercentageCharge(categoryCode = '') {
  return STANDARD_TARIFF_PERCENTAGE_CODES.includes(categoryCode);
}

function isStandardTariffBaseCategory(category = {}) {
  const categoryCode = getCategoryCode(category);
  const chargeType = getCategoryChargeType(category);

  if (chargeType === 'percentage') {
    return false;
  }

  return isBaseChargeCategory(category)
    || isStandardTariffBaseCharge(categoryCode)
    || ['unit', 'demand', 'fixed'].includes(chargeType);
}

function isStandardTariffPercentageCategory(category = {}) {
  const categoryCode = getCategoryCode(category);
  const chargeType = getCategoryChargeType(category);

  return chargeType === 'percentage'
    || (!isBaseChargeCategory(category) && isStandardTariffPercentageCharge(categoryCode));
}

function calculateStandardTariffBaseAmount({
  category,
  rate,
  totalConsumption,
  kvaDemand,
  billingPeriod,
}) {
  const chargeType = getCategoryChargeType(category);

  switch (chargeType) {
    case 'unit':
      return totalConsumption * getRateValue(rate);
    case 'demand':
      return kvaDemand * getRateValue(rate);
    case 'fixed':
      return isDailyRate(rate)
        ? getRateValue(rate) * calculateBillingDays(billingPeriod)
        : getRateValue(rate);
    default:
      return getRateValue(rate);
  }
}

function getPercentageBaseAmount(category = {}, calculatedCharges = {}, subtotal = 0) {
  const categoryCode = getCategoryCode(category);
  const appliesTo = getPercentageAppliesTo(category);
  const dependencies = appliesTo.length
    ? appliesTo
    : getDefaultPercentageDependencies(categoryCode);

  if (!dependencies.length) {
    return subtotal;
  }

  return dependencies.reduce(
    (sum, baseCategory) => sum + (parseFloat(calculatedCharges[baseCategory]) || 0),
    0,
  );
}

function calculateStandardTariffBill({
  consumptionData = {},
  kvaDemand = 0,
  categories = [],
  rates = {},
  ratesUsed = [],
  totalConsumption = 0,
} = {}) {
  const baseResult = categories.reduce((result, category) => {
    const categoryCode = getCategoryCode(category);
    if (!isStandardTariffBaseCategory(category)) {
      return result;
    }

    const rate = rates[categoryCode];
    if (!rate) {
      return result;
    }

    const amount = calculateStandardTariffBaseAmount({
      category,
      rate,
      totalConsumption,
      kvaDemand,
      billingPeriod: consumptionData.billingPeriod,
    });

    const chargeAmount = Number.isFinite(amount) ? amount : 0;

    return {
      calculatedCharges: {
        ...result.calculatedCharges,
        [categoryCode]: chargeAmount,
      },
      subtotal: result.subtotal + chargeAmount,
    };
  }, {
    calculatedCharges: {},
    subtotal: 0,
  });

  const calculatedCharges = categories.reduce((charges, category) => {
    const categoryCode = getCategoryCode(category);
    if (!isStandardTariffPercentageCategory(category)) {
      return charges;
    }

    const rate = rates[categoryCode];
    if (!rate) {
      return charges;
    }

    const percentageBaseAmount = getPercentageBaseAmount(
      category,
      charges,
      baseResult.subtotal,
    );
    const amount = (percentageBaseAmount * getRateValue(rate)) / 100;

    return {
      ...charges,
      [categoryCode]: Number.isFinite(amount) ? amount : 0,
    };
  }, baseResult.calculatedCharges);

  calculatedCharges.totalCalculated = Object.values(calculatedCharges).reduce(
    (sum, amount) => sum + (parseFloat(amount) || 0),
    0,
  );

  return {
    calculatedCharges,
    ratesUsed,
    subtotal: baseResult.subtotal,
    calculationMode: 'STANDARD_TARIFF',
  };
}

export { calculateStandardTariffBill };
export default calculateStandardTariffBill;
